/**
 * Recipe section editor (Steg 1c-ii + 1c-iii) — edit the nested ingredient +
 * method content of a recipe and REORDER it by drag-and-drop.
 * Mirrors the prototype (recipe-d-seksjoner.html): two cards (① Ingredienser,
 * ② Fremgangsmåte), each a list of named sections with a divider-pill title.
 *
 * - Method step NUMBERS are GLOBAL and continuous across sections (1,2,3,…),
 *   recomputed on every render by flattening the step sections in order.
 * - New sections/rows get a stable client id (`crypto.randomUUID`). These ids
 *   are reorder keys only — the server NEVER trusts them for authorization
 *   (the cookbook role gates the write; see the 1a deny tests).
 * - User text is rendered as React text nodes only (no dangerouslySetInnerHTML).
 *
 * Drag-and-drop (1c-iii, this slice): reorder ingredient rows and method steps
 * WITHIN and ACROSS sections of the same kind, via **dnd-kit** (pointer + touch
 * + keyboard — the prototype README warns native HTML5 DnD is unreliable in
 * Chrome). Each kind gets its own DndContext, so an ingredient can never land in
 * a step section. Ingredient rows drag from the ⠿ grip; steps drag from their
 * number circle; WHOLE sections drag from the ⠿ grip on the header (an outer
 * SortableContext of section ids over the per-item ones — the active id, a
 * section id vs an item id, tells the two apart). The reorder logic is pure +
 * unit-tested (`moveOnDragOver` / `moveOnDragEnd` / `reorderSections` /
 * `createSectionDragHandlers`); the dnd-kit wiring is thin glue verified in the
 * browser.
 *
 * State: the section arrays live in the editor's form state (so autosave can
 * send them); every mutation produces a NEW array via the `onChange(next, save)`
 * callback. Live cross-section moves during a drag pass `save=false` (no autosave
 * churn mid-drag); the drop passes `save=true`. Blank rows/sections are pruned at
 * SAVE time (`pruneIngredientSections` / `pruneStepSections`), not here.
 */

import {
  type IngredientItem,
  type IngredientSection,
  type StepItem,
  type StepItemWrite,
  type StepSection,
  type StepSectionWrite,
  formatIngredientAmount,
} from "@tjommi/shared";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useRef, useState } from "react";

function newId(): string {
  return crypto.randomUUID();
}

export function emptyIngredientItem(): IngredientItem {
  return { id: newId(), quantity: null, unit: "", name: "" };
}
export function emptyStepItem(): StepItem {
  return { id: newId(), text: "", image: null };
}
/** A fresh ingredient section seeded with one blank row (type immediately). */
export function emptyIngredientSection(): IngredientSection {
  return { id: newId(), name: "", items: [emptyIngredientItem()] };
}
/** A fresh method section seeded with one blank step. */
export function emptyStepSection(): StepSection {
  return { id: newId(), name: "", items: [emptyStepItem()] };
}

const isBlankIngredient = (it: IngredientItem): boolean =>
  it.quantity === null && it.unit.trim() === "" && it.name.trim() === "";
const isBlankStep = (it: StepItem): boolean => it.text.trim() === "";

/**
 * Strip `imageUrl` — a signed GET URL populated ONLY on read responses (see
 * `StepItem.imageUrl` in `@tjommi/shared`) — before a step is sent on ANY
 * write (create/update). The API's `ValidationPipe` is `whitelist: true` +
 * `forbidNonWhitelisted: true`, so a response-only field on the wire 400s the
 * WHOLE request. Explicitly pick the writable fields (`id`, `text`, `image`)
 * rather than deleting the property off the live object, so the still-`image`
 * key is never accidentally dropped and the form's own state (which the UI
 * needs to keep rendering the thumbnail) is left untouched.
 *
 * The `StepItemWrite` return type (the read `StepItem` minus the response-only
 * `imageUrl`) makes this a COMPILE error if someone ever puts `imageUrl` back
 * into the returned object literal — the structural guard for this class of bug.
 */
function toWriteStep(it: StepItem): StepItemWrite {
  return { id: it.id, text: it.text, image: it.image ?? null };
}

/** Drop blank rows, then drop sections that have neither a name nor any rows. */
export function pruneIngredientSections(secs: IngredientSection[]): IngredientSection[] {
  return secs
    .map((s) => ({ ...s, items: s.items.filter((it) => !isBlankIngredient(it)) }))
    .filter((s) => s.name.trim() !== "" || s.items.length > 0);
}
/**
 * Drop blank rows/sections AND map every remaining step to its write-only
 * shape (see `toWriteStep`) — this is the single mapping point every write
 * path (autosave + both image-write PATCHes) funnels stepSections through
 * before calling `createRecipe`/`updateRecipe`, so `imageUrl` can never reach
 * the API on a write.
 */
export function pruneStepSections(secs: StepSection[]): StepSectionWrite[] {
  return secs
    .map((s) => ({
      ...s,
      items: s.items.filter((it) => !isBlankStep(it)).map(toWriteStep),
    }))
    .filter((s) => s.name.trim() !== "" || s.items.length > 0);
}

/** A section is "empty" (deletable without confirmation) when it has no name and no filled rows. */
function ingredientSectionEmpty(s: IngredientSection): boolean {
  return s.name.trim() === "" && s.items.every(isBlankIngredient);
}
function stepSectionEmpty(s: StepSection): boolean {
  return s.name.trim() === "" && s.items.every(isBlankStep);
}

// ------------------------------------------------------------ Drag reorder (pure)

/** A section as far as drag reorder cares: an id + an ordered list of items. */
type ItemSection<T> = { id: string; name: string; items: T[] };

/** Index of the section that owns `id` — matching a section (container) id OR an item id. */
function containerIndexOf<T extends { id: string }>(secs: ItemSection<T>[], id: string): number {
  const asContainer = secs.findIndex((s) => s.id === id);
  if (asContainer !== -1) return asContainer;
  return secs.findIndex((s) => s.items.some((it) => it.id === id));
}

/**
 * Live move DURING a drag: only acts when `active` and `over` are in DIFFERENT
 * sections (same-section reorder is finalized on drop). Returns the next sections
 * with the active item spliced into the target section, or null for a no-op.
 */
export function moveOnDragOver<T extends { id: string }>(
  secs: ItemSection<T>[],
  activeId: string,
  overId: string | null,
): ItemSection<T>[] | null {
  if (!overId || activeId === overId) return null;
  const from = containerIndexOf(secs, activeId);
  const to = containerIndexOf(secs, overId);
  if (from === -1 || to === -1 || from === to) return null;

  const fromSec = secs[from]!;
  const ai = fromSec.items.findIndex((it) => it.id === activeId);
  if (ai === -1) return null;
  const item = fromSec.items[ai]!;

  const toSec = secs[to]!;
  const overIdx = toSec.items.findIndex((it) => it.id === overId);
  const insert = overIdx === -1 ? toSec.items.length : overIdx; // over a container ⇒ append

  const next = secs.slice();
  next[from] = { ...fromSec, items: fromSec.items.filter((_, i) => i !== ai) };
  const toItems = toSec.items.slice();
  toItems.splice(insert, 0, item);
  next[to] = { ...toSec, items: toItems };
  return next;
}

/**
 * Finalize on drop: reorder within the (now shared, after any over-move) section,
 * or — as a fallback for paths that skip onDragOver — move across sections.
 * Returns the next sections, or null when nothing changes.
 */
export function moveOnDragEnd<T extends { id: string }>(
  secs: ItemSection<T>[],
  activeId: string,
  overId: string | null,
): ItemSection<T>[] | null {
  if (!overId) return null;
  const from = containerIndexOf(secs, activeId);
  const to = containerIndexOf(secs, overId);
  if (from === -1 || to === -1) return null;

  if (from !== to) return moveOnDragOver(secs, activeId, overId);

  const sec = secs[from]!;
  const oldIndex = sec.items.findIndex((it) => it.id === activeId);
  const newIndex =
    sec.id === overId ? sec.items.length - 1 : sec.items.findIndex((it) => it.id === overId);
  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return null;

  const next = secs.slice();
  next[from] = { ...sec, items: arrayMove(sec.items, oldIndex, newIndex) };
  return next;
}

/** Reorder WHOLE sections (Slice 2). `overId` may be a section id or an item id
 * inside the target section — both resolve to the target section. */
export function reorderSections<T extends { id: string }>(
  secs: ItemSection<T>[],
  activeId: string,
  overId: string | null,
): ItemSection<T>[] | null {
  if (!overId) return null;
  const from = secs.findIndex((s) => s.id === activeId);
  const to = containerIndexOf(secs, overId);
  if (from === -1 || to === -1 || from === to) return null;
  return arrayMove(secs, from, to);
}

/**
 * Build the dnd-kit event handlers for one card. Pure + injectable (no dnd-kit
 * runtime needed) so the reorder wiring is unit-testable: pass fake events, a
 * mock `onChange`, `setActiveId`, and a `changed` ref-box. The active id tells a
 * WHOLE-section drag (id is a section id) from a row/step drag (id is an item id)
 * — section and item ids are distinct client UUIDs.
 */
export function createSectionDragHandlers<T extends { id: string }>(
  sections: ItemSection<T>[],
  onChange: (next: ItemSection<T>[], save?: boolean) => void,
  setActiveId: (id: string | null) => void,
  changed: { current: boolean },
): {
  onDragStart: (e: DragStartEvent) => void;
  onDragOver: (e: DragOverEvent) => void;
  onDragEnd: (e: DragEndEvent) => void;
  onDragCancel: () => void;
} {
  const isSection = (id: string): boolean => sections.some((s) => s.id === id);
  return {
    onDragStart: ({ active }) => {
      changed.current = false;
      setActiveId(String(active.id));
    },
    onDragOver: ({ active, over }) => {
      const activeId = String(active.id);
      if (isSection(activeId)) return; // section reorder is finalized on drop
      const next = moveOnDragOver(sections, activeId, over ? String(over.id) : null);
      if (next) {
        onChange(next, false); // live move: don't autosave mid-drag
        changed.current = true;
      }
    },
    onDragEnd: ({ active, over }) => {
      const activeId = String(active.id);
      const overId = over ? String(over.id) : null;
      const next = isSection(activeId)
        ? reorderSections(sections, activeId, overId)
        : moveOnDragEnd(sections, activeId, overId);
      if (next) onChange(next, true);
      else if (changed.current) onChange(sections, true); // persist a cross-section move
      changed.current = false;
      setActiveId(null);
    },
    onDragCancel: () => {
      changed.current = false;
      setActiveId(null);
    },
  };
}

/** Norwegian screen-reader copy for keyboard drag (dnd-kit accessibility). */
const A11Y = {
  screenReaderInstructions: {
    draggable:
      "Trykk mellomrom eller enter for å løfte elementet. Bruk piltastene for å flytte det, " +
      "mellomrom eller enter for å slippe, Escape for å avbryte.",
  },
  announcements: {
    onDragStart: () => "Løftet elementet. Bruk piltastene for å flytte.",
    onDragOver: () => "Elementet flyttes.",
    onDragEnd: () => "Slapp elementet på ny plass.",
    onDragCancel: () => "Avbrøt flyttingen.",
  },
};

function useSectionDnd<T extends { id: string }>(
  sections: ItemSection<T>[],
  onChange: (next: ItemSection<T>[], save?: boolean) => void,
): {
  activeId: string | null;
  sensors: ReturnType<typeof useSensors>;
  handlers: ReturnType<typeof createSectionDragHandlers<T>>;
} {
  const [activeId, setActiveId] = useState<string | null>(null);
  const changed = useRef(false);
  const sensors = useSensors(
    // A small distance so a click on the handle still lets the inputs be used.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handlers = createSectionDragHandlers(sections, onChange, setActiveId, changed);
  return { activeId, sensors, handlers };
}

// -----------------------------------------------------------------------------

export function SectionsEditor({
  ingredientSections,
  stepSections,
  onIngredientChange,
  onStepChange,
  stepUploading,
  stepErrors,
  onStepImagePick,
  onStepImageRemove,
}: {
  ingredientSections: IngredientSection[];
  stepSections: StepSection[];
  onIngredientChange: (next: IngredientSection[], save?: boolean) => void;
  onStepChange: (next: StepSection[], save?: boolean) => void;
  /** Keyed by step client id — true while that step's image is uploading. (Steg 3b.) */
  stepUploading?: Record<string, boolean>;
  /** Keyed by step client id — an inline error message for that step's image, or null/undefined. */
  stepErrors?: Record<string, string | null>;
  /** A file was picked for a step's image (camera/plus icon next to "✕ Fjern steg"). */
  onStepImagePick?: (stepId: string, file: File) => void;
  /** The remove control on a step's image thumbnail was clicked. */
  onStepImageRemove?: (stepId: string) => void;
}): React.JSX.Element {
  // Transient: the id of an input that should grab focus once it mounts (set
  // when a new section/row is added). autoFocus only fires on mount, so an
  // already-rendered input is never re-focused by a later focusId change.
  const [focusId, setFocusId] = useState<string | null>(null);

  return (
    <>
      <IngredientCard
        sections={ingredientSections}
        focusId={focusId}
        onChange={onIngredientChange}
        setFocusId={setFocusId}
      />
      <StepCard
        sections={stepSections}
        focusId={focusId}
        onChange={onStepChange}
        setFocusId={setFocusId}
        stepUploading={stepUploading}
        stepErrors={stepErrors}
        onImagePick={onStepImagePick}
        onImageRemove={onStepImageRemove}
      />
    </>
  );
}

// --------------------------------------------------------------- Ingredients ①

function IngredientCard({
  sections,
  focusId,
  onChange,
  setFocusId,
}: {
  sections: IngredientSection[];
  focusId: string | null;
  onChange: (next: IngredientSection[], save?: boolean) => void;
  setFocusId: (id: string) => void;
}): React.JSX.Element {
  const dnd = useSectionDnd(sections, onChange);

  const setSection = (si: number, next: IngredientSection): void =>
    onChange(sections.map((s, i) => (i === si ? next : s)));

  const renameSection = (si: number, name: string): void =>
    setSection(si, { ...sections[si]!, name });

  const deleteSection = (si: number): void => {
    const sec = sections[si]!;
    if (!ingredientSectionEmpty(sec) && !confirmDelete(sec.name)) return;
    onChange(sections.filter((_, i) => i !== si));
  };

  const addSection = (): void => {
    const sec = emptyIngredientSection();
    onChange([...sections, sec]);
    setFocusId(sec.id);
  };

  const addRow = (si: number): void => {
    const row = emptyIngredientItem();
    setSection(si, { ...sections[si]!, items: [...sections[si]!.items, row] });
    setFocusId(row.id);
  };

  const setRow = (si: number, ri: number, patch: Partial<IngredientItem>): void => {
    const sec = sections[si]!;
    setSection(si, {
      ...sec,
      items: sec.items.map((it, i) => (i === ri ? { ...it, ...patch } : it)),
    });
  };

  const deleteRow = (si: number, ri: number): void => {
    const sec = sections[si]!;
    setSection(si, { ...sec, items: sec.items.filter((_, i) => i !== ri) });
  };

  const active = dnd.activeId
    ? sections.flatMap((s) => s.items).find((it) => it.id === dnd.activeId)
    : null;

  return (
    <div className="card card-pad" data-testid="editor-ingredients">
      <div className="card-h">
        <h3>Ingredienser</h3>
      </div>

      <DndContext
        sensors={dnd.sensors}
        collisionDetection={closestCorners}
        accessibility={A11Y}
        onDragStart={dnd.handlers.onDragStart}
        onDragOver={dnd.handlers.onDragOver}
        onDragEnd={dnd.handlers.onDragEnd}
        onDragCancel={dnd.handlers.onDragCancel}
      >
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {sections.map((sec, si) => (
            <SortableSection
              key={sec.id}
              id={sec.id}
              header={{
                name: sec.name,
                autoFocus: sec.id === focusId,
                nameTestId: `ing-sec-name-${si}`,
                delTestId: `ing-sec-del-${si}`,
                gripTestId: `ing-sec-grip-${si}`,
                onRename: (name) => renameSection(si, name),
                onDelete: () => deleteSection(si),
              }}
            >
              <SortableContext
                items={sec.items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="sec-ing-list">
                  {sec.items.map((it, ri) => (
                    <SortableIngredientRow
                      key={it.id}
                      item={it}
                      si={si}
                      ri={ri}
                      autoFocus={it.id === focusId}
                      onQuantity={(v) => setRow(si, ri, { quantity: v })}
                      onUnit={(v) => setRow(si, ri, { unit: v })}
                      onName={(v) => setRow(si, ri, { name: v })}
                      onDelete={() => deleteRow(si, ri)}
                    />
                  ))}
                </div>
              </SortableContext>
              <button
                type="button"
                className="add-line"
                data-testid={`ing-add-row-${si}`}
                onClick={() => addRow(si)}
              >
                ＋ Legg til ingrediens
              </button>
            </SortableSection>
          ))}
        </SortableContext>

        <DragOverlay>
          {active ? (
            <div className="ing-row is-overlay">
              <span className="ing-grip" aria-hidden="true">
                ⠿
              </span>
              <span className="input">{formatIngredientAmount(active)}</span>
              <span className="input">{active.name}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <div>
        <button
          type="button"
          className="add-sec-btn"
          data-testid="ing-add-section"
          onClick={addSection}
        >
          ＋ Legg til seksjon
        </button>
      </div>
    </div>
  );
}

/**
 * Numeric "Mengde" input. The model field is `number | null`, but a plain
 * controlled number input can't hold a half-typed decimal ("2,") — so we keep a
 * local string mirror, push the PARSED number up on every change, and re-sync the
 * mirror only when the incoming `value` no longer matches what the mirror parses
 * to (e.g. autosave/import seeding a different value). Accepts a Norwegian comma
 * or a dot; empty ⇒ null.
 */
function parseQuantityInput(raw: string): number | null {
  const t = raw.trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}
function quantityToInput(value: number | null): string {
  return value === null ? "" : String(value).replace(".", ",");
}
function QuantityInput({
  value,
  onChange,
  autoFocus,
  testId,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  autoFocus: boolean;
  testId: string;
}): React.JSX.Element {
  const [text, setText] = useState(() => quantityToInput(value));
  const [focused, setFocused] = useState(false);
  // Reflect an EXTERNAL value change (load, import seeding, autosave-normalized)
  // only while the field is idle — never clobber a half-typed "2," under the cursor.
  useEffect(() => {
    if (!focused) setText(quantityToInput(value));
  }, [value, focused]);
  return (
    <input
      className="input ing-qty"
      placeholder="Mengde"
      aria-label="Mengde"
      inputMode="decimal"
      data-testid={testId}
      autoFocus={autoFocus}
      value={text}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setText(quantityToInput(value)); // re-normalize "2," → "2" on leave
      }}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseQuantityInput(e.target.value));
      }}
    />
  );
}

function SortableIngredientRow({
  item,
  si,
  ri,
  autoFocus,
  onQuantity,
  onUnit,
  onName,
  onDelete,
}: {
  item: IngredientItem;
  si: number;
  ri: number;
  autoFocus: boolean;
  onQuantity: (v: number | null) => void;
  onUnit: (v: string) => void;
  onName: (v: string) => void;
  onDelete: () => void;
}): React.JSX.Element {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`ing-row sortable${isDragging ? " dragging" : ""}`}
      data-testid={`ing-row-${si}-${ri}`}
    >
      <button
        type="button"
        className="ing-grip"
        aria-label="Dra for å flytte ingrediensen"
        data-testid={`ing-grip-${si}-${ri}`}
        {...attributes}
        {...listeners}
      >
        ⠿
      </button>
      <QuantityInput
        value={item.quantity}
        onChange={onQuantity}
        autoFocus={autoFocus}
        testId={`ing-amount-${si}-${ri}`}
      />
      <input
        className="input ing-unit"
        placeholder="Enhet"
        aria-label="Enhet"
        data-testid={`ing-unit-${si}-${ri}`}
        value={item.unit}
        onChange={(e) => onUnit(e.target.value)}
      />
      <input
        className="input"
        placeholder="Ingrediens"
        aria-label="Ingrediens"
        data-testid={`ing-name-${si}-${ri}`}
        value={item.name}
        onChange={(e) => onName(e.target.value)}
      />
      <button
        type="button"
        className="ing-del"
        aria-label="Fjern ingrediens"
        data-testid={`ing-del-${si}-${ri}`}
        onClick={onDelete}
      >
        ✕
      </button>
    </div>
  );
}

// ------------------------------------------------------------------- Method ②

function StepCard({
  sections,
  focusId,
  onChange,
  setFocusId,
  stepUploading,
  stepErrors,
  onImagePick,
  onImageRemove,
}: {
  sections: StepSection[];
  focusId: string | null;
  onChange: (next: StepSection[], save?: boolean) => void;
  setFocusId: (id: string) => void;
  stepUploading?: Record<string, boolean>;
  stepErrors?: Record<string, string | null>;
  onImagePick?: (stepId: string, file: File) => void;
  onImageRemove?: (stepId: string) => void;
}): React.JSX.Element {
  const dnd = useSectionDnd(sections, onChange);

  const setSection = (si: number, next: StepSection): void =>
    onChange(sections.map((s, i) => (i === si ? next : s)));

  const renameSection = (si: number, name: string): void =>
    setSection(si, { ...sections[si]!, name });

  const deleteSection = (si: number): void => {
    const sec = sections[si]!;
    if (!stepSectionEmpty(sec) && !confirmDelete(sec.name)) return;
    onChange(sections.filter((_, i) => i !== si));
  };

  const addSection = (): void => {
    const sec = emptyStepSection();
    onChange([...sections, sec]);
    setFocusId(sec.id);
  };

  const addStep = (si: number): void => {
    const step = emptyStepItem();
    setSection(si, { ...sections[si]!, items: [...sections[si]!.items, step] });
    setFocusId(step.id);
  };

  const setStep = (si: number, ri: number, text: string): void => {
    const sec = sections[si]!;
    setSection(si, { ...sec, items: sec.items.map((it, i) => (i === ri ? { ...it, text } : it)) });
  };

  const deleteStep = (si: number, ri: number): void => {
    const sec = sections[si]!;
    setSection(si, { ...sec, items: sec.items.filter((_, i) => i !== ri) });
  };

  // Global continuous step numbers: the running count of steps before each section.
  const startNumbers: number[] = [];
  let run = 0;
  for (const sec of sections) {
    startNumbers.push(run);
    run += sec.items.length;
  }

  const active = dnd.activeId
    ? sections.flatMap((s) => s.items).find((it) => it.id === dnd.activeId)
    : null;
  const activeNumber = active
    ? sections.flatMap((s) => s.items).findIndex((it) => it.id === active.id) + 1
    : 0;

  return (
    <div className="card card-pad" data-testid="editor-steps">
      <div className="card-h">
        <h3>Fremgangsmåte</h3>
      </div>

      <DndContext
        sensors={dnd.sensors}
        collisionDetection={closestCorners}
        accessibility={A11Y}
        onDragStart={dnd.handlers.onDragStart}
        onDragOver={dnd.handlers.onDragOver}
        onDragEnd={dnd.handlers.onDragEnd}
        onDragCancel={dnd.handlers.onDragCancel}
      >
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {sections.map((sec, si) => (
            <SortableSection
              key={sec.id}
              id={sec.id}
              header={{
                name: sec.name,
                autoFocus: sec.id === focusId,
                nameTestId: `step-sec-name-${si}`,
                delTestId: `step-sec-del-${si}`,
                gripTestId: `step-sec-grip-${si}`,
                onRename: (name) => renameSection(si, name),
                onDelete: () => deleteSection(si),
              }}
            >
              <SortableContext
                items={sec.items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="sec-step-list">
                  {sec.items.map((step, ri) => (
                    <SortableStep
                      key={step.id}
                      step={step}
                      si={si}
                      ri={ri}
                      number={startNumbers[si]! + ri + 1}
                      autoFocus={step.id === focusId}
                      onText={(v) => setStep(si, ri, v)}
                      onDelete={() => deleteStep(si, ri)}
                      uploading={stepUploading?.[step.id] ?? false}
                      error={stepErrors?.[step.id] ?? null}
                      onImagePick={onImagePick ? (file) => onImagePick(step.id, file) : undefined}
                      onImageRemove={onImageRemove ? () => onImageRemove(step.id) : undefined}
                    />
                  ))}
                </div>
              </SortableContext>
              <button
                type="button"
                className="add-line"
                data-testid={`step-add-row-${si}`}
                onClick={() => addStep(si)}
              >
                ＋ Legg til steg
              </button>
            </SortableSection>
          ))}
        </SortableContext>

        <DragOverlay>
          {active ? (
            <div className="step is-overlay">
              <div className="step-n">{activeNumber}</div>
              <div className="step-body">
                <div className="textarea">{active.text}</div>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <div>
        <button
          type="button"
          className="add-sec-btn"
          data-testid="step-add-section"
          onClick={addSection}
        >
          ＋ Legg til seksjon
        </button>
      </div>
    </div>
  );
}

function SortableStep({
  step,
  si,
  ri,
  number,
  autoFocus,
  onText,
  onDelete,
  uploading = false,
  error = null,
  onImagePick,
  onImageRemove,
}: {
  step: StepItem;
  si: number;
  ri: number;
  number: number;
  autoFocus: boolean;
  onText: (v: string) => void;
  onDelete: () => void;
  /** Steg 3b: per-step image upload state + handlers (optional — read view has none). */
  uploading?: boolean;
  error?: string | null;
  onImagePick?: (file: File) => void;
  onImageRemove?: () => void;
}): React.JSX.Element {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: step.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasImage = Boolean(step.imageUrl);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (file) onImagePick?.(file);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`step${isDragging ? " dragging" : ""}`}
      data-testid={`step-${si}-${ri}`}
    >
      <button
        type="button"
        className="step-n"
        aria-label={`Dra for å flytte steg ${number}`}
        data-testid={`step-n-${si}-${ri}`}
        {...attributes}
        {...listeners}
      >
        {number}
      </button>
      <div className="step-body">
        <textarea
          className="textarea"
          placeholder="Beskriv steget …"
          aria-label={`Steg ${number}`}
          data-testid={`step-text-${si}-${ri}`}
          autoFocus={autoFocus}
          value={step.text}
          onChange={(e) => onText(e.target.value)}
        />

        {hasImage && (
          <div className="step-thumb-wrap">
            <img
              src={step.imageUrl ?? undefined}
              alt=""
              className="step-thumb"
              data-testid={`step-image-${si}-${ri}`}
            />
            {!uploading && onImageRemove && (
              <button
                type="button"
                className="step-thumb-remove"
                aria-label="Fjern steg-bilde"
                data-testid={`step-image-remove-${si}-${ri}`}
                onClick={onImageRemove}
              >
                ✕
              </button>
            )}
          </div>
        )}

        <div className="step-tools">
          {onImagePick && (
            <button
              type="button"
              className="step-image-btn"
              aria-label={hasImage ? "Bytt bilde av steget" : "Legg til bilde av steget"}
              data-testid={`step-image-add-${si}-${ri}`}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? "Laster opp …" : hasImage ? "Bytt bilde" : "Legg til bilde"}
            </button>
          )}
          <button
            type="button"
            className="step-del-btn"
            data-testid={`step-del-${si}-${ri}`}
            onClick={onDelete}
          >
            ✕ Fjern steg
          </button>
        </div>
        {onImagePick && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="visually-hidden"
            data-testid={`step-image-input-${si}-${ri}`}
            onChange={handleFileChange}
          />
        )}
        {error && (
          <p className="image-error" data-testid={`step-image-error-${si}-${ri}`} role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- Shared pieces

interface HeaderProps {
  name: string;
  autoFocus: boolean;
  nameTestId: string;
  delTestId: string;
  gripTestId: string;
  onRename: (name: string) => void;
  onDelete: () => void;
}

/**
 * A whole section, draggable as a unit (Slice 2): its useSortable node also acts
 * as the droppable container for row/step moves (over.id === section id), so no
 * separate droppable is needed. The section grip lives in the header.
 */
function SortableSection({
  id,
  header,
  children,
}: {
  id: string;
  header: HeaderProps;
  children: React.ReactNode;
}): React.JSX.Element {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className={`sec-group${isDragging ? " dragging" : ""}`}>
      <SectionHeader {...header} gripAttributes={attributes} gripListeners={listeners} />
      {children}
    </div>
  );
}

/** The divider-pill section header: a ⠿ drag grip + editable name + delete button. */
function SectionHeader({
  name,
  autoFocus,
  nameTestId,
  delTestId,
  gripTestId,
  onRename,
  onDelete,
  gripAttributes,
  gripListeners,
}: HeaderProps & {
  gripAttributes?: React.HTMLAttributes<HTMLButtonElement>;
  gripListeners?: Record<string, unknown>;
}): React.JSX.Element {
  return (
    <div className="sec-hdr">
      <div className="sec-line" />
      <button
        type="button"
        className="sec-grip"
        aria-label="Dra for å flytte seksjonen"
        data-testid={gripTestId}
        {...gripAttributes}
        {...gripListeners}
      >
        ⠿
      </button>
      <div className="sec-pill">
        <input
          className="sec-title"
          placeholder="Seksjonsnavn (valgfritt)"
          aria-label="Seksjonsnavn"
          data-testid={nameTestId}
          autoFocus={autoFocus}
          value={name}
          onChange={(e) => onRename(e.target.value)}
        />
        <button
          type="button"
          className="sec-del"
          aria-label="Slett seksjon"
          title="Slett seksjon"
          data-testid={delTestId}
          onClick={onDelete}
        >
          ✕
        </button>
      </div>
      <div className="sec-line" />
    </div>
  );
}

/** Confirm before discarding a section that still holds content (no undo). */
function confirmDelete(name: string): boolean {
  const label = name.trim() === "" ? "seksjonen" : `«${name.trim()}»`;
  return window.confirm(`Slette ${label} og innholdet?`);
}
